import React, { useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import TextureOverlay from '../components/team/TextureOverlay';
// Static imports: the page renders in one pass, so the prerendered HTML is replaced
// by identical markup on load instead of a loading screen.
import SiteHeader from '../components/team/SiteHeader';
import HeroAndStats from '../components/team/HeroAndStats';
import ProductShowcase from '../components/team/ProductShowcase';
import LogisticsDelivery from '../components/team/LogisticsDelivery';
import CoffeeSpecsAndPricing from '../components/team/CoffeeSpecsAndPricing';
import HowToOrder from '../components/team/HowToOrder';
import OurStory from '../components/team/OurStory';
import TasteAndBrewing from '../components/team/TasteAndBrewing';
import FAQ from '../components/team/FAQ';
import TeamAndContact from '../components/team/TeamAndContact';
import Footer from '../components/team/Footer';
import SectionDivider from '../components/team/SectionDivider';

const meta = {
  title: 'Mubarak Farm | Uganda Robusta Coffee, Farm to Pakistan',
  meta: [
    { name: 'description', content: 'Mubarak Farm grows single-origin Uganda Robusta in Luweero and imports it to Karachi. Green beans SC-12, SC-15, SC-18 by the kg, delivered in 2-3 days.' },
    { name: 'keywords', content: 'robusta coffee pakistan, karachi coffee wholesale, ugandan coffee, duty paid coffee, bulk coffee karachi, moonshine coffee, halal coffee, premium coffee pakistan, coffee wholesale karachi, ugandan robusta pakistan, Mubarak Farm, Screen 18 coffee, Screen 15 coffee, Screen 12 coffee, SC-18 SC-15 SC-12 green beans, customs cleared coffee' },
    { name: 'author', content: 'Mubarak Farm Uganda' },
    { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
    { property: 'og:title', content: 'Mubarak Farm | Uganda Robusta Coffee, Farm to Pakistan' },
    { property: 'og:description', content: 'Mubarak Farm grows single-origin Uganda Robusta in Luweero and imports it to Karachi. Green beans SC-12, SC-15, SC-18 by the kg, delivered in 2-3 days.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://mubarakfarmug.com/' },
    { property: 'og:image', content: 'https://mubarakfarmug.com/flow-assets/content/og-mubarak-farm.jpg' },
    { property: 'og:locale', content: 'en_GB' },
    { property: 'og:site_name', content: 'Mubarak Farm Uganda' },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:type', content: 'image/jpeg' },
    { property: 'og:image:alt', content: 'Ripening coffee cherries on a branch' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Mubarak Farm | Uganda Robusta Coffee, Farm to Pakistan' },
    { name: 'twitter:description', content: 'Mubarak Farm grows single-origin Uganda Robusta in Luweero and imports it to Karachi. Green beans SC-12, SC-15, SC-18 by the kg, delivered in 2-3 days.' },
    { name: 'twitter:image', content: 'https://mubarakfarmug.com/flow-assets/content/og-mubarak-farm.jpg' },
    { name: 'geo.region', content: 'UG-116' },
    { name: 'geo.placename', content: 'Luweero' },
    { name: 'geo.position', content: '0.8403414;32.4977066' },
    { name: 'ICBM', content: '0.8403414, 32.4977066' },
  ],
  link: [
    { rel: 'icon', type: 'image/png', href: '/favicon.png' },
    { rel: 'canonical', href: 'https://mubarakfarmug.com/' },
  ],
  style: [],
  script: [
    // JSON-LD Structured Data. Wholesale entities (green-bean Product, WholesaleStore,
    // Service) live on /wholesale so each page describes its own content.
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": "https://mubarakfarmug.com/#organization",
        "name": "Mubarak Farm",
        "alternateName": "Mubarak Farm Uganda",
        "url": "https://mubarakfarmug.com/",
        "logo": "https://mubarakfarmug.com/flow-assets/logos/mubarak-logo.png",
        "description": "Mubarak Farm grows single-origin Uganda Robusta coffee in Luweero District and supplies green and roasted beans to buyers in Pakistan from stock held in Karachi.",
        "foundingDate": "2018",
        "founder": {
          "@type": "Person",
          "name": "Quaidjohar Hatim"
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Nabinaka Road",
          "addressLocality": "Luweero",
          "addressRegion": "Nabutaka",
          "addressCountry": "UG"
        },
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "telephone": "+256-707-776-564",
            "email": "mubarakfarm53@gmail.com",
            "contactType": "customer service",
            "areaServed": "UG",
            "availableLanguage": "English"
          },
          {
            "@type": "ContactPoint",
            "telephone": "+44-771-608-9293",
            "contactType": "sales",
            "areaServed": "PK",
            "availableLanguage": "English"
          }
        ],
        "sameAs": [
          "https://www.instagram.com/mubarak.farming.ug",
          "https://maps.google.com/?cid=7459800105914995170"
        ]
      })
    },
    // Retail roasted coffee. No offers: retail prices are not shown on the page.
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "Moonshine Coffee, Roasted Uganda Robusta",
        "brand": {
          "@type": "Brand",
          "name": "Moonshine Coffee"
        },
        "manufacturer": {
          "@type": "Organization",
          "@id": "https://mubarakfarmug.com/#organization"
        },
        "description": "Moonshine Coffee is a single-origin Robusta roasted from beans grown by Mubarak Farm in Nabutaka, Luweero District, Uganda. Sold as whole bean or ground.",
        "category": "Coffee & Tea > Coffee Beans",
        "image": "https://mubarakfarmug.com/flow-assets/content/moonshine-coffee.webp",
        "sku": "MUBARAK-ROBUSTA-SC121518",
        "productID": "mubarak-farm-robusta-coffee",
        "additionalProperty": [
          { "@type": "PropertyValue", "name": "Coffee Type", "value": "Robusta" },
          { "@type": "PropertyValue", "name": "Origin", "value": "Uganda, East Africa" },
          { "@type": "PropertyValue", "name": "Region", "value": "Central Uganda" },
          { "@type": "PropertyValue", "name": "Farm Location", "value": "Nabutaka, Luweero District" },
          { "@type": "PropertyValue", "name": "Altitude", "value": "1100-1200 MASL" },
          { "@type": "PropertyValue", "name": "Harvest Date", "value": "February 2026" },
          { "@type": "PropertyValue", "name": "Process", "value": "Natural, sun dried on raised beds" },
          { "@type": "PropertyValue", "name": "Tasting Notes", "value": "Dark chocolate, woody, earthy, full body" },
          { "@type": "PropertyValue", "name": "Roast Type", "value": "Medium" },
          { "@type": "PropertyValue", "name": "Grind Options", "value": "Whole Bean or Ground" }
        ]
      })
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": "https://mubarakfarmug.com/#farm",
        "name": "Mubarak Farm Uganda",
        "alternateName": "Mubarak Coffee Farm",
        "description": "Family-owned coffee farm in Nabutaka, Luweero District, Uganda, growing single-origin Robusta graded as Screen 12, 15 and 18.",
        "url": "https://mubarakfarmug.com/",
        "image": "https://mubarakfarmug.com/flow-assets/content/visitor-area.jpg",
        "telephone": "+256-707-776-564",
        "email": "mubarakfarm53@gmail.com",
        "parentOrganization": {
          "@type": "Organization",
          "@id": "https://mubarakfarmug.com/#organization"
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Nabinaka Road",
          "addressLocality": "Luweero",
          "addressRegion": "Nabutaka",
          "addressCountry": "UG"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 0.8403414,
          "longitude": 32.4977066
        },
        "hasMap": "https://maps.google.com/?cid=7459800105914995170",
        "sameAs": ["https://maps.google.com/?cid=7459800105914995170"],
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "17:00"
          }
        ],
        "foundingDate": "2018",
        "founder": {
          "@type": "Person",
          "name": "Quaidjohar Hatim"
        }
      })
    },
    // FAQ Schema
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Is this coffee Halal?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. It is pure Robusta with no additives or processing agents, so it is suitable for Halal consumption."
            }
          },
          {
            "@type": "Question",
            "name": "Do I have to pay customs or import duties?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No. We hold local stock in both Pakistan and Uganda. Your order is dispatched domestically from our warehouses in Karachi or Kampala, so there are no hidden fees."
            }
          },
          {
            "@type": "Question",
            "name": "How do I place an order?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We process orders personally. Please click the WhatsApp button or contact us to request your order. We will confirm the final total based on your shipping location."
            }
          },
          {
            "@type": "Question",
            "name": "What are the payment options?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We accept Cash on Delivery (COD) and Bank Transfer."
            }
          },
          {
            "@type": "Question",
            "name": "How long will delivery take?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Delivery takes 2-3 days from our Karachi warehouse."
            }
          },
          {
            "@type": "Question",
            "name": "Do you sell whole beans or ground coffee?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We sell both. You can order roasted whole beans (if you have a grinder) or pre-ground coffee ready for brewing."
            }
          },
          {
            "@type": "Question",
            "name": "What if my package arrives damaged?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "If your package is damaged upon arrival, simply send us a photo. We will ship a replacement free of charge."
            }
          }
        ]
      })
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": "https://mubarakfarmug.com/#website",
        "name": "Mubarak Farm",
        "url": "https://mubarakfarmug.com/",
        "publisher": {
          "@type": "Organization",
          "@id": "https://mubarakfarmug.com/#organization"
        },
        "inLanguage": "en"
      })
    }
  ],
};

export default function Index() {
  // A /#section link from another page can arrive before its target is laid out.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    let tries = 0;
    const timer = setInterval(() => {
      const target = document.getElementById(id);
      if (target || ++tries > 30) {
        clearInterval(timer);
        if (target) target.scrollIntoView();
      }
    }, 100);
    return () => clearInterval(timer);
  }, []);

  return (
    <React.Fragment>
      <HelmetProvider>
        <Helmet {...meta}></Helmet>
      </HelmetProvider>
      <TextureOverlay fixed />
      <SiteHeader overlay />
      <main>
        <HeroAndStats />
        <ProductShowcase />
        <SectionDivider />
        <LogisticsDelivery />
        <SectionDivider />
        <CoffeeSpecsAndPricing />
        <SectionDivider />
        <HowToOrder />
        <SectionDivider />
        <OurStory />
        <SectionDivider />
        <TasteAndBrewing />
        <SectionDivider />
        <FAQ />
        <SectionDivider />
        <TeamAndContact />
      </main>
      <Footer />
    </React.Fragment>
  );
}